import React from 'react';

import {CodonTranslationTable} from 'biojs/BioConstants';
import {GeneticCodeData} from 'biojs/Codons';
import geneticCodesDNA from 'data/geneticCodesDNA.json';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {ellipsize} from 'util/NameUtil';
import sortedBy from 'util/sortedBy';

const NO_CHANGE_TEXT = 'Keep existing genetic codes';

interface GeneticCodeDropdownProps {
  className?: string;
  geneticCode: CodonTranslationTable | null;
  onSelectGeneticCode: (geneticCode: CodonTranslationTable | null) => void;
  disabled?: boolean;
  includeNullOption?: boolean;
  buttonSize?: ButtonSize;
}

/**
 * Dropdown component for selecting a genetic code for translations
 */
export default function GeneticCodeDropdown({
  className,
  geneticCode,
  onSelectGeneticCode,
  disabled,
  includeNullOption,
  buttonSize,
}: GeneticCodeDropdownProps): JSX.Element {
  const codeOptions: Array<OptionData> = sortedBy(Object.values(CodonTranslationTable), [
    (code) => geneticCodesDNA[code].number,
  ]).map((code: CodonTranslationTable) => {
    // DNA vs RNA table doesn't matter here since we're only accessing metadata, not the codon tables.
    const {name, number, taxonomy} = geneticCodesDNA[code] as GeneticCodeData;
    const formattedTaxonomy = taxonomy.join(', ');
    return {
      name: `${number}. ${name}`,
      value: code,
      metadata: ellipsize(formattedTaxonomy, 45),
      tooltipTitle: formattedTaxonomy === '' ? null : formattedTaxonomy,
    };
  });
  const keepExistingOption = {name: NO_CHANGE_TEXT, value: null};
  const optionDatas = includeNullOption ? [keepExistingOption, ...codeOptions] : codeOptions;

  return (
    <SelectDropdown
      className={className}
      buttonSize={buttonSize}
      displayName={geneticCode ? geneticCodesDNA[geneticCode].name : NO_CHANGE_TEXT}
      menu={
        <SearchableMenu
          scrollable={true}
          onSelect={onSelectGeneticCode}
          selectedValue={geneticCode}
          optionDatas={optionDatas}
          getMatchingOptionDatas={(query) =>
            optionDatas.filter(({value}) => {
              if (value == null) {
                // Only include null option when there is no search query.
                return query === '';
              }
              const {number, taxonomy, name} = geneticCodesDNA[
                value as keyof typeof geneticCodesDNA
              ] as GeneticCodeData;
              const q = query.toLowerCase();
              return (
                number.toString().includes(q) ||
                name.toLowerCase().includes(q) ||
                taxonomy.some((taxonomyName) => taxonomyName.toLowerCase().includes(q))
              );
            })
          }
          height={MenuHeight.MEDIUM}
          width={MenuWidth.LARGE}
          spellCheck={false}
        />
      }
      disabled={disabled}
    />
  );
}
